import React from 'react';
import { ArrowDown, ArrowUp, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { DropdownMenu } from '../ui/DropdownMenu';
import { EPCBadge, PerformanceBar } from './BuildingBadges';

const COLUMNS = [
  { id: 'name',        label: 'Name' },
  { id: 'city',        label: 'City' },
  { id: 'type',        label: 'Type' },
  { id: 'area',        label: 'Floor area', align: 'right' },
  { id: 'performance', label: 'Performance' },
  { id: 'epc',         label: 'EPC', estimated: true },
];

const areaText = (b) => (b.area ? `${b.area.toLocaleString()} m²` : '—');

const RowActions = ({ building, onEdit, onDelete }) => {
  const items = [
    onEdit && { id: 'edit', label: 'Edit', icon: Pencil, onSelect: () => onEdit(building) },
    onEdit && onDelete && { type: 'separator' },
    onDelete && { id: 'delete', label: 'Delete…', icon: Trash2, danger: true, onSelect: () => onDelete(building) },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <DropdownMenu
      label={`Actions for ${building.name}`}
      width="w-40"
      items={items}
      renderTrigger={({ triggerProps }) => (
        <button
          type="button"
          aria-label={`Actions for ${building.name}`}
          className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-transparent border-0 text-lb-muted hover:bg-black/[0.05] focus-ring"
          {...triggerProps}
        >
          <MoreHorizontal size={16} aria-hidden="true" />
        </button>
      )}
    />
  );
};

/**
 * Sortable building table (desktop) and stacked list (phone). Opening a
 * building is a row click or the name button; Edit and Delete live in the
 * row's ⋯ menu and are omitted when the handler is not given.
 */
export const BuildingTable = ({ buildings, sort, dir, onSort, openId, onOpen, onEdit, onDelete }) => (
  <>
    {/* No horizontal scroll container: it would clip the row menus. At the
        widths the table shows (md and up) it fits, with long names wrapping. */}
    <div className="card-base hidden md:block">
      <table className="w-full text-[13px] border-collapse">
        <thead>
          <tr>
            {COLUMNS.map((column) => {
              const active = sort === column.id;
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={[
                    'px-4 h-10 text-[12px] font-medium text-lb-muted border-b border-black/[0.06]',
                    column.align === 'right' ? 'text-right' : 'text-left',
                  ].join(' ')}
                  title={column.estimated ? 'Estimated from the performance score until a certified EPC is available' : undefined}
                >
                  <button
                    type="button"
                    onClick={() => onSort(column.id)}
                    className={[
                      'inline-flex items-center gap-1 p-0 bg-transparent border-0 font-medium focus-ring',
                      active ? 'text-lb-ink' : 'text-lb-muted hover:text-lb-ink',
                    ].join(' ')}
                  >
                    {column.label}
                    {column.estimated && <span className="text-lb-muted-soft font-normal">(est.)</span>}
                    {active && (dir === 'asc'
                      ? <ArrowUp size={12} aria-hidden="true" />
                      : <ArrowDown size={12} aria-hidden="true" />)}
                  </button>
                </th>
              );
            })}
            <th scope="col" className="w-12 border-b border-black/[0.06]"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {buildings.map((b) => (
            <tr
              key={b.id}
              onClick={() => onOpen(b)}
              className="cursor-pointer hover:bg-black/[0.02] transition-colors"
              style={{ background: openId === b.id ? 'rgba(61,107,56,0.06)' : undefined }}
            >
              <td className="px-4 h-12 border-b border-black/[0.05]">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpen(b); }}
                  className="p-0 bg-transparent border-0 text-left font-semibold text-lb-ink-strong hover:underline focus-ring"
                >
                  {b.name}
                </button>
              </td>
              <td className="px-4 border-b border-black/[0.05] text-lb-muted">{b.city || '—'}</td>
              <td className="px-4 border-b border-black/[0.05] text-lb-muted">{b.building_type || b.type || '—'}</td>
              <td className="px-4 border-b border-black/[0.05] text-right tabular-nums text-lb-ink">{areaText(b)}</td>
              <td className="px-4 border-b border-black/[0.05]">
                <PerformanceBar value={b.performance == null ? null : b.performanceScore} />
              </td>
              <td className="px-4 border-b border-black/[0.05]"><EPCBadge rating={b.epc} compact /></td>
              <td className="px-2 border-b border-black/[0.05] text-right" onClick={(e) => e.stopPropagation()}>
                <RowActions building={b} onEdit={onEdit} onDelete={onDelete} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="m-0 px-4 py-2.5 text-[12px] text-lb-muted">
        {buildings.length} {buildings.length === 1 ? 'building' : 'buildings'}
      </p>
    </div>

    <ul className="card-base md:hidden list-none m-0 p-0 divide-y divide-black/[0.06]" aria-label="Buildings">
      {buildings.map((b) => (
        <li key={b.id}>
          <button
            type="button"
            onClick={() => onOpen(b)}
            className="w-full px-4 py-3 text-left bg-transparent border-0 focus-ring"
          >
            <span className="flex items-center justify-between gap-2">
              <span className="text-[14px] font-semibold text-lb-ink-strong truncate">{b.name}</span>
              <EPCBadge rating={b.epc} compact />
            </span>
            <span className="block mt-0.5 text-[12px] text-lb-muted">
              {[b.city, b.building_type || b.type, b.area ? areaText(b) : null].filter(Boolean).join(' · ')}
            </span>
            <span className="block mt-2">
              <PerformanceBar value={b.performance == null ? null : b.performanceScore} />
            </span>
          </button>
        </li>
      ))}
    </ul>
  </>
);
